import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, ConfigProvider, Drawer, Empty, Input, Popconfirm, Select, Spin, message,
} from 'antd';
import { useStore } from './store/store';
import { hexToRgba } from './utils/theme';
import InfoCardPreview from './components/RightSide/configurationModal/InfoCardPreview';
import InfoCardFields from './components/common/boxStyleFields/InfoCardFields';
import BoxStyleTemplatePicker from './components/common/boxStyleFields/BoxStyleTemplatePicker';
import { boxPropsFromStyle } from './shared/boxes/styleTemplate';
import {
  INFO_CARD_STYLE_PRESETS,
  infoCardPresetTemplates,
  infoCardStylePatch,
} from './shared/boxes/infoCard/infoCardStyles';
import { defaultInfoCardConfiguration } from './constants/constants';
import {
  deleteCardStyle, getCardStyles, patchCardStyle, postCardStyle,
} from './requests/cardStyles';
import './proposalApp.css';
import './costingEmbed.css';

/**
 * Écran d'admin de la banque de modèles de Carte info, en plein écran et sans
 * le reste de l'éditeur. Embarqué en iframe dans le costing (admin → Banque de
 * cartes) via EDITOR_URL?jwt=<jwt>&view=card-bank — même mécanique que
 * LibraryApp, et pour la même raison : une carte se DESSINE avec les
 * composants de l'éditeur. Les recopier côté costing aurait donné deux
 * interfaces d'édition à tenir en phase, et obligé à stocker une vignette de
 * chaque carte faute de savoir la rendre.
 *
 * Aucun document n'est ouvert ici : la palette de couleurs proposée est donc
 * celle par défaut (`defaultConfiguration.colors`), comme dans le compositeur
 * de formes — les couleurs de marque vivent dans la configuration de chaque
 * modèle, pas au niveau de la société.
 */
const COSTING_ACCENT = '#2166AB'; // blue06 de la palette costing (theme.js)

// Métier fictif du sélecteur de gauche : les modèles que personne n'a rangés
// dans un métier, visibles partout.
const GENERAL_KEY = 'general';
const ALL_KEY = 'all';

const PRESET_TEMPLATES = infoCardPresetTemplates();

/** Le texte de démonstration d'un modèle, faute de contenu réel à montrer. */
const sampleOf = (props) => props?.sample ?? defaultInfoCardConfiguration.sample;

const CardPreview = ({ props, width = 320 }) => (
  <InfoCardPreview style={props} width={width} sample={sampleOf(props)} />
);

const columnStyle = {
  width: 220,
  flexShrink: 0,
  borderRight: '1px solid rgba(11, 34, 57, 0.12)',
  padding: 12,
  overflowY: 'auto',
};

const workRowStyle = (active) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 8px',
  borderRadius: 6,
  cursor: 'pointer',
  marginBottom: 2,
  backgroundColor: active ? hexToRgba(COSTING_ACCENT, 0.12) : 'transparent',
  color: active ? COSTING_ACCENT : 'inherit',
  fontWeight: active ? 600 : 400,
});

const cardTileStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 12,
  border: '1px solid rgba(11, 34, 57, 0.12)',
  borderRadius: 8,
  backgroundColor: 'white',
};

const CardBankApp = () => {
  // Le métier est stocké sous son nom technique (« guarding ») et se lit
  // traduit partout dans l'éditeur, par une clé à plat (cf. NewTemplateModal).
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [works, setWorks] = useState([]);
  const [styles, setStyles] = useState([]);
  const [selectedWork, setSelectedWork] = useState(ALL_KEY);
  // null | { id, name, props, companyWorkId } — le modèle en cours d'édition,
  // recopié le temps du panneau : on n'écrit en base qu'à l'enregistrement.
  const [edited, setEdited] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const configuration = useStore(({ configuration }) => configuration);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCardStyles();
      setWorks(data.works ?? []);
      setStyles(data.card_styles ?? []);
    } catch (error) {
      message.error("La banque de cartes n'a pas pu être chargée");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', COSTING_ACCENT);
    document.documentElement.style.setProperty('--primary-color-light', hexToRgba(COSTING_ACCENT, 0.3));
    // Boutons du tiroir/popovers antd, portés sur <body> (cf. costingEmbed.css).
    document.body.classList.add('costing-embed-popups');
  }, []);

  const visibleStyles = useMemo(() => {
    if (selectedWork === ALL_KEY) return styles;
    if (selectedWork === GENERAL_KEY) return styles.filter((s) => s.company_work_id === null);
    return styles.filter((s) => s.company_work_id === selectedWork);
  }, [styles, selectedWork]);

  const countFor = (key) => {
    if (key === ALL_KEY) return styles.length;
    if (key === GENERAL_KEY) return styles.filter((s) => s.company_work_id === null).length;
    return styles.filter((s) => s.company_work_id === key).length;
  };

  // Les conversions qu'attend InfoCardFields : l'interface raisonne en labels
  // de palette, le modèle stocke des chaînes CSS (cf. useBoxPropsEditor).
  const labelOfColor = (cssColor) => configuration.colors
    .find((c) => c.style === cssColor)?.label ?? 'transparent';
  const colorOfLabel = (label) => (label === 'transparent'
    ? ''
    : configuration.colors.find((c) => c.label === label)?.style ?? '');

  const openNew = () => {
    // Le métier courant devient celui du modèle : on crée là où on regarde.
    const companyWorkId = [ALL_KEY, GENERAL_KEY].includes(selectedWork) ? null : selectedWork;
    setEdited({
      id: null,
      name: '',
      props: boxPropsFromStyle(defaultInfoCardConfiguration),
      companyWorkId,
    });
    setCreating(true);
  };

  const openEdit = (style) => {
    setEdited({
      id: style.id,
      name: style.name,
      props: { ...style.props },
      companyWorkId: style.company_work_id,
    });
    setCreating(false);
  };

  const duplicate = async (style) => {
    try {
      await postCardStyle({
        name: `${style.name} - Copie`,
        props: style.props,
        companyWorkId: style.company_work_id,
      });
      message.success('Modèle dupliqué');
      reload();
    } catch (error) {
      message.error("Le modèle n'a pas pu être dupliqué");
    }
  };

  const remove = async (style) => {
    try {
      await deleteCardStyle(style.id);
      message.success('Modèle supprimé');
      reload();
    } catch (error) {
      message.error("Le modèle n'a pas pu être supprimé");
    }
  };

  const save = async () => {
    const name = edited.name.trim();
    if (!name || saving) return;
    setSaving(true);
    try {
      if (edited.id) {
        await patchCardStyle(edited.id, {
          name, props: edited.props, companyWorkId: edited.companyWorkId,
        });
      } else {
        await postCardStyle({ name, props: edited.props, companyWorkId: edited.companyWorkId });
      }
      message.success('Modèle enregistré');
      setEdited(null);
      reload();
    } catch (error) {
      message.error("Le modèle n'a pas pu être enregistré");
    } finally {
      setSaving(false);
    }
  };

  const setProp = (key, value) => setEdited((current) => ({
    ...current, props: { ...current.props, [key]: value },
  }));

  const workOptions = [
    { value: null, label: 'Général (tous les métiers)' },
    ...works.map((work) => ({ value: work.id, label: t(work.name) })),
  ];

  return (
    <ConfigProvider theme={{ token: { colorPrimary: COSTING_ACCENT } }}>
      <div className="costing-embed" style={{ display: 'flex', height: '100vh', background: 'white' }}>
        <div style={columnStyle}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>
            Métiers
          </div>
          <div style={workRowStyle(selectedWork === ALL_KEY)} onClick={() => setSelectedWork(ALL_KEY)}>
            <span>Toutes</span><span>{countFor(ALL_KEY)}</span>
          </div>
          <div style={workRowStyle(selectedWork === GENERAL_KEY)} onClick={() => setSelectedWork(GENERAL_KEY)}>
            <span>Général</span><span>{countFor(GENERAL_KEY)}</span>
          </div>
          {works.map((work) => (
            <div
              key={work.id}
              style={workRowStyle(selectedWork === work.id)}
              onClick={() => setSelectedWork(work.id)}
            >
              <span>{t(work.name)}</span><span>{countFor(work.id)}</span>
            </div>
          ))}
        </div>

        <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
              Modèles de cartes
            </div>
            <Button type="primary" onClick={openNew}>Nouveau modèle</Button>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>}

          {!loading && !visibleStyles.length && (
            <Empty description="Aucun modèle dans ce métier" />
          )}

          {!loading && !!visibleStyles.length && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 16,
            }}>
              {visibleStyles.map((style) => (
                <div key={style.id} style={cardTileStyle}>
                  {/* L'aperçu est une vraie carte, redessinée depuis ses
                      valeurs — pas une vignette enregistrée. */}
                  <div style={{
                    height: 150, overflow: 'hidden', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
                  }}>
                    <div style={{ transform: 'scale(0.6)', transformOrigin: 'center' }}>
                      <CardPreview props={style.props} />
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{style.name}</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Button size="small" onClick={() => openEdit(style)}>Modifier</Button>
                    <Button size="small" onClick={() => duplicate(style)}>Dupliquer</Button>
                    <Popconfirm
                      title="Supprimer ce modèle ?"
                      description="Les documents déjà mis en forme ne changent pas."
                      okText="Supprimer"
                      cancelText="Annuler"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => remove(style)}
                    >
                      <Button size="small" danger>Supprimer</Button>
                    </Popconfirm>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Drawer
          open={!!edited}
          width={420}
          title={creating ? 'Nouveau modèle de carte' : 'Modifier le modèle'}
          onClose={() => setEdited(null)}
          destroyOnHidden
          extra={(
            <Button
              type="primary"
              loading={saving}
              disabled={!edited?.name.trim()}
              onClick={save}
            >
              Enregistrer
            </Button>
          )}
        >
          {edited && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{
                display: 'flex', justifyContent: 'center', padding: 12,
                background: 'rgba(11, 34, 57, 0.04)', borderRadius: 8, pointerEvents: 'none',
              }}>
                <div style={{ transform: 'scale(0.8)', transformOrigin: 'center' }}>
                  <CardPreview props={edited.props} width={300} />
                </div>
              </div>

              <Input
                placeholder="Nom du modèle"
                value={edited.name}
                onChange={(e) => setEdited((c) => ({ ...c, name: e.target.value }))}
              />
              <Select
                value={edited.companyWorkId ?? null}
                options={workOptions}
                onChange={(value) => setEdited((c) => ({ ...c, companyWorkId: value }))}
              />

              {/* Partir d'un modèle de la galerie, comme dans le
                  configurateur : composer une carte de zéro demande de
                  régler quinze champs. */}
              {creating && (
                <BoxStyleTemplatePicker
                  styles={PRESET_TEMPLATES}
                  props={edited.props}
                  onApply={(preset) => {
                    const def = INFO_CARD_STYLE_PRESETS.find((p) => p.key === preset.id);
                    if (!def) return;
                    setEdited((c) => ({
                      ...c,
                      props: { ...c.props, ...infoCardStylePatch(def), sample: def.sample },
                    }));
                  }}
                  renderPreview={(preset) => <CardPreview props={preset} />}
                />
              )}

              <InfoCardFields
                style={edited.props}
                setProp={setProp}
                labelOfColor={labelOfColor}
                colorOfLabel={colorOfLabel}
              />
            </div>
          )}
        </Drawer>
      </div>
    </ConfigProvider>
  );
};

export default CardBankApp;
